"use client";

import {
  Fragment,
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type KeyboardEvent,
  type ReactNode,
} from "react";
import { useFormStatus } from "react-dom";
import { enableCallsAction, sendMessageAction } from "@frontend/lib/actions";
import { clientApi, publicApiUrl } from "@frontend/lib/api";
import { useRouter } from "@frontend/lib/navigation";
import { partyActorsForMessages } from "@frontend/lib/system-actors";
import { isHiddenSystemMessage, parseSystemEvent } from "@frontend/lib/system-events";
import type { Message } from "@creators/backend/db/schema";
import type { OfferView } from "@creators/backend/lib/collabs/queries";
import type { Party } from "@creators/backend/lib/collabs/state-machine";
import { dayKey, formatDateTime, formatDayLabel, formatTime } from "@creators/backend/lib/time";

const POLL_MS = 3_000;
const STALE_AFTER_FAILURES = 3;
/** Keep in sync with backend MAX_MESSAGE_ATTACHMENT_BYTES. */
const MAX_CHAT_ATTACHMENT_BYTES = 5 * 1024 * 1024;

const ALLOWED_CHAT_ATTACHMENT_MIMES = new Set([
  "image/png",
  "image/jpeg",
  "image/webp",
  "image/gif",
  "application/pdf",
  "audio/mp4",
  "audio/m4a",
  "audio/x-m4a",
  "audio/aac",
  "audio/mpeg",
  "audio/mp3",
]);

function resolveChatMime(file: File): string {
  if (file.type) return file.type.toLowerCase();
  const name = file.name.toLowerCase();
  if (name.endsWith(".png")) return "image/png";
  if (name.endsWith(".jpg") || name.endsWith(".jpeg")) return "image/jpeg";
  if (name.endsWith(".webp")) return "image/webp";
  if (name.endsWith(".gif")) return "image/gif";
  if (name.endsWith(".pdf")) return "application/pdf";
  return file.type;
}

function chatAttachmentError(file: File): string | null {
  if (!file.size || file.size <= 0) return "That file looks empty.";
  if (file.size > MAX_CHAT_ATTACHMENT_BYTES) {
    return "That file is too large — 5 MB is the limit.";
  }
  if (!ALLOWED_CHAT_ATTACHMENT_MIMES.has(resolveChatMime(file))) {
    return "Use a PNG, JPEG, WebP, GIF, PDF, or voice note.";
  }
  return null;
}

type MessageRow = Message & {
  /** Client-only — optimistic delivery state (WhatsApp-style). */
  clientStatus?: "sending" | "failed";
};

const OPTIMISTIC_PREFIX = "optimistic:";

function isOptimisticId(id: string): boolean {
  return id.startsWith(OPTIMISTIC_PREFIX);
}

function mergeMessages(current: MessageRow[], incoming: MessageRow[]): MessageRow[] {
  if (incoming.length === 0) return current;
  const seen = new Set(current.map((m) => m.id));
  const merged = [...current];
  for (const message of incoming) {
    if (!seen.has(message.id)) merged.push(message);
  }
  const realKeys = new Set(
    merged
      .filter((m) => !isOptimisticId(m.id))
      .map((m) => {
        const attachments = Array.isArray(m.attachments) ? m.attachments.length : 0;
        return `${m.sender}|${(m.body ?? "").trim()}|${attachments}`;
      }),
  );
  const cleaned = merged.filter((m) => {
    if (!isOptimisticId(m.id)) return true;
    const attachments = Array.isArray(m.attachments) ? m.attachments.length : 0;
    const key = `${m.sender}|${(m.body ?? "").trim()}|${attachments}`;
    return !realKeys.has(key);
  });
  cleaned.sort((a, b) => {
    const byTime = new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime();
    if (byTime !== 0) return byTime;
    // Chat copy before system lines when clocks collide.
    const aSys = a.eventType ? 1 : 0;
    const bSys = b.eventType ? 1 : 0;
    if (aSys !== bSys) return aSys - bSys;
    return a.id.localeCompare(b.id);
  });
  return cleaned;
}

function latestTimestamp(rows: MessageRow[]): string | null {
  const last = [...rows].reverse().find((m) => !isOptimisticId(m.id));
  if (!last) return null;
  return new Date(last.createdAt).toISOString();
}

function isNearBottom(el: HTMLElement, threshold = 96): boolean {
  return el.scrollHeight - el.scrollTop - el.clientHeight < threshold;
}

export function Thread({
  collabId,
  messages: initialMessages,
  viewer,
  creatorName,
  brandName,
  offerHistory = [],
  acceptedBy = null,
  canPost,
  closedReason,
  showReadReceipts = false,
  counterpartLastReadAt: initialCounterpartLastReadAt = null,
  callRequestPending = false,
  composerAccessory,
  isActive = true,
  stickSignal,
}: {
  collabId: string;
  messages: MessageRow[];
  viewer: "creator" | "brand" | "admin";
  creatorName: string;
  brandName: string;
  offerHistory?: OfferView[];
  acceptedBy?: Party | null;
  canPost: boolean;
  /** Shown under the thread when posting is disabled. */
  closedReason?: string;
  /** Priority boosts only — blue double-ticks when the other side has read. */
  showReadReceipts?: boolean;
  counterpartLastReadAt?: Date | string | null;
  /** Collaborator asked to talk — show Allow on that activity line for the creator. */
  callRequestPending?: boolean;
  /** Renders above the composer (e.g. Accept / Counter / Decline). */
  composerAccessory?: ReactNode;
  /** When Chat tab becomes active again, pin the list to the latest message. */
  isActive?: boolean;
  /** Changes when chrome above the list appears (e.g. action banner) — re-pin to bottom. */
  stickSignal?: string | number | boolean;
}) {
  const [messages, setMessages] = useState(initialMessages);
  const [counterpartLastReadAt, setCounterpartLastReadAt] = useState<Date | null>(() =>
    initialCounterpartLastReadAt ? new Date(initialCounterpartLastReadAt) : null,
  );
  const [stale, setStale] = useState(false);
  const listRef = useRef<HTMLDivElement>(null);
  const messagesRef = useRef(messages);
  const stickToBottomRef = useRef(true);
  const didMountRef = useRef(false);
  const failuresRef = useRef(0);
  const inFlightRef = useRef(false);
  const outboundRef = useRef(new Map<string, { body: string; file: File | null }>());
  const partyNames = { creator: creatorName, brand: brandName };
  const actorsByMessageId = partyActorsForMessages(messages, offerHistory, acceptedBy);

  messagesRef.current = messages;

  const showReadReceiptsRef = useRef(showReadReceipts);
  showReadReceiptsRef.current = showReadReceipts;

  const markFailed = useCallback((tempId: string) => {
    setMessages((current) =>
      current.map((m) => (m.id === tempId ? { ...m, clientStatus: "failed" as const } : m)),
    );
  }, []);

  const dropOptimistic = useCallback((tempId: string) => {
    outboundRef.current.delete(tempId);
    setMessages((current) => current.filter((m) => m.id !== tempId));
  }, []);

  const syncMessages = useCallback(async () => {
    if (inFlightRef.current) return;
    inFlightRef.current = true;
    const since = latestTimestamp(messagesRef.current);
    const query = since ? `?since=${encodeURIComponent(since)}` : "";
    try {
      const data = await clientApi<{
        messages: MessageRow[];
        counterpartLastReadAt?: Date | string | null;
      }>(`/api/collabs/${collabId}/messages${query}`);
      failuresRef.current = 0;
      setStale(false);
      setMessages((current) => mergeMessages(current, data.messages));
      if (showReadReceiptsRef.current && data.counterpartLastReadAt != null) {
        const next = new Date(data.counterpartLastReadAt);
        if (!Number.isNaN(next.getTime())) {
          setCounterpartLastReadAt((prev) =>
            !prev || next.getTime() >= prev.getTime() ? next : prev,
          );
        }
      }
    } catch {
      failuresRef.current += 1;
      if (failuresRef.current >= STALE_AFTER_FAILURES) setStale(true);
    } finally {
      inFlightRef.current = false;
    }
  }, [collabId]);

  const resendOptimistic = useCallback(
    async (tempId: string) => {
      const payload = outboundRef.current.get(tempId);
      if (!payload) return;
      setMessages((current) =>
        current.map((m) => (m.id === tempId ? { ...m, clientStatus: "sending" as const } : m)),
      );
      const formData = new FormData();
      formData.set("collabId", collabId);
      formData.set("body", payload.body);
      if (payload.file) formData.set("attachment", payload.file);
      const result = await sendMessageAction({}, formData);
      if (result.error && !result.ok) {
        markFailed(tempId);
        return;
      }
      dropOptimistic(tempId);
      stickToBottomRef.current = true;
      await syncMessages();
    },
    [collabId, dropOptimistic, markFailed, syncMessages],
  );

  // Soft room refreshes pass a new array reference with the same tip — do not
  // wipe locally polled messages or remount the scroller every few seconds.
  const seedSig = `${initialMessages.length}:${initialMessages.at(-1)?.id ?? ""}`;
  useEffect(() => {
    setMessages((current) => {
      if (current.length === 0) return initialMessages;
      const tip = initialMessages.at(-1)?.id;
      if (tip && current.some((row) => row.id === tip)) {
        return mergeMessages(initialMessages, current);
      }
      return initialMessages;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [seedSig]);

  useEffect(() => {
    if (!initialCounterpartLastReadAt) return;
    const next = new Date(initialCounterpartLastReadAt);
    if (Number.isNaN(next.getTime())) return;
    setCounterpartLastReadAt((prev) =>
      !prev || next.getTime() >= prev.getTime() ? next : prev,
    );
  }, [initialCounterpartLastReadAt]);

  useEffect(() => {
    const tick = () => {
      if (document.visibilityState === "visible") void syncMessages();
    };
    const id = window.setInterval(tick, POLL_MS);
    document.addEventListener("visibilitychange", tick);
    return () => {
      window.clearInterval(id);
      document.removeEventListener("visibilitychange", tick);
    };
  }, [syncMessages]);

  const scrollToEnd = useCallback(() => {
    const el = listRef.current;
    if (!el) return;
    stickToBottomRef.current = true;
    const pin = () => {
      el.scrollTop = el.scrollHeight;
    };
    pin();
    // Second pass after layout (images / multi-line bubbles / tab show).
    requestAnimationFrame(() => {
      pin();
      requestAnimationFrame(pin);
    });
    window.setTimeout(pin, 50);
    window.setTimeout(pin, 160);
    window.setTimeout(pin, 320);
  }, []);

  // Details ↔ Chat, or action banner appearing above the list: stay on the tip.
  useLayoutEffect(() => {
    if (!isActive) return;
    scrollToEnd();
  }, [isActive, stickSignal, scrollToEnd]);

  // Viewport shrinks when the banner mounts — content height is unchanged.
  useLayoutEffect(() => {
    const el = listRef.current;
    if (!el || typeof ResizeObserver === "undefined") return;
    const ro = new ResizeObserver(() => {
      if (stickToBottomRef.current) scrollToEnd();
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, [scrollToEnd]);

  useLayoutEffect(() => {
    const el = listRef.current;
    if (!el) return;

    if (!didMountRef.current) {
      didMountRef.current = true;
      el.scrollTop = el.scrollHeight;
      return;
    }

    if (stickToBottomRef.current) {
      scrollToEnd();
    }
  }, [messages, scrollToEnd]);

  const handleListScroll = () => {
    const el = listRef.current;
    if (!el || !isActive) return;
    if (el.clientHeight < 8) return;
    stickToBottomRef.current = isNearBottom(el);
  };

  return (
    <section className="flex min-h-0 flex-1 flex-col overflow-hidden">
      {/* Absolute scroller so the message count never dictates the pane's height. */}
      <div className="relative min-h-0 flex-1">
        {stale ? <ReconnectingBanner /> : null}

        <div
          ref={listRef}
          onScroll={handleListScroll}
          className="absolute inset-0 overflow-y-auto overscroll-contain [scrollbar-gutter:stable]"
        >
          {/* A short thread rests on the composer rather than floating at the top. */}
          <ol className="flex min-h-full flex-col justify-end px-2.5 py-4 lg:px-4">
            {(() => {
              const visible = messages.filter((message) => !isHiddenSystemMessage(message));
              if (visible.length === 0) {
                return (
                  <li className="flex flex-1 items-center justify-center px-5 py-12 text-center text-sm text-ink-muted">
                    No messages yet — say hello.
                  </li>
                );
              }
              return visible.map((message, index) => {
                const previous = index > 0 ? visible[index - 1] : null;
                const next = index < visible.length - 1 ? visible[index + 1] : null;
                const newDay =
                  !previous || dayKey(previous.createdAt) !== dayKey(message.createdAt);
                const nextNewDay = next
                  ? dayKey(next.createdAt) !== dayKey(message.createdAt)
                  : true;
                const mine = message.sender === viewer;
                const readByCounterpart =
                  showReadReceipts &&
                  mine &&
                  counterpartLastReadAt != null &&
                  new Date(message.createdAt).getTime() <= counterpartLastReadAt.getTime();

                return (
                  <Fragment key={message.id}>
                    {newDay ? <DaySeparator at={message.createdAt} /> : null}
                    {message.sender === "platform" ? (
                      <ActivityLine
                        collabId={collabId}
                        message={message}
                        names={partyNames}
                        viewer={viewer}
                        actorParty={actorsByMessageId.get(message.id) ?? null}
                        connectTop={!newDay && previous?.sender === "platform"}
                        connectBottom={!nextNewDay && next?.sender === "platform"}
                        showAllowCall={
                          callRequestPending &&
                          viewer === "creator" &&
                          canPost &&
                          (message.eventType === "call.requested" ||
                            /asked to talk on a call|call requested/i.test(
                              message.body ?? "",
                            ))
                        }
                      />
                    ) : (
                      <ChatBubble
                        collabId={collabId}
                        message={message}
                        mine={mine}
                        author={message.sender === "creator" ? creatorName : brandName}
                        showReadReceipts={
                          showReadReceipts && mine && !message.clientStatus
                        }
                        read={readByCounterpart}
                        onResend={
                          message.clientStatus === "failed"
                            ? () => void resendOptimistic(message.id)
                            : undefined
                        }
                      />
                    )}
                  </Fragment>
                );
              });
            })()}
          </ol>
        </div>
      </div>

      {composerAccessory}

      {canPost ? (
        <div className="shrink-0 border-t border-line bg-surface px-3 py-3 lg:px-6">
          <MessageForm
            collabId={collabId}
            viewer={viewer === "admin" ? "creator" : viewer}
            onOptimistic={(row, payload) => {
              outboundRef.current.set(row.id, payload);
              setMessages((current) => [...current, { ...row, clientStatus: "sending" }]);
              // Always pin to bottom on send — even if the user had scrolled up.
              scrollToEnd();
            }}
            onFailed={markFailed}
            onSent={dropOptimistic}
            onSettled={syncMessages}
          />
        </div>
      ) : closedReason ? (
        <div className="flex shrink-0 justify-center border-t border-line bg-surface px-3 py-2.5 lg:px-6">
          <span className="inline-flex items-center gap-1.5 rounded-full border border-line bg-canvas px-3 py-1.5 text-xs font-medium text-ink-muted">
            <LockIcon />
            {closedReason}
          </span>
        </div>
      ) : null}
    </section>
  );
}

function ReconnectingBanner() {
  return (
    <div
      role="status"
      className="pointer-events-none absolute inset-x-0 top-0 z-10 flex justify-center p-2"
    >
      <span className="inline-flex items-center gap-1.5 rounded-full bg-warn-50 px-2.5 py-1 text-[11px] font-medium text-warn-700 shadow-sm ring-1 ring-warn-100">
        <span className="size-1.5 animate-pulse rounded-full bg-warn-600" aria-hidden="true" />
        Reconnecting…
      </span>
    </div>
  );
}

function MessageForm({
  collabId,
  viewer,
  onOptimistic,
  onFailed,
  onSent,
  onSettled,
}: {
  collabId: string;
  viewer: "creator" | "brand";
  onOptimistic: (
    row: MessageRow,
    payload: { body: string; file: File | null },
  ) => void;
  onFailed: (tempId: string) => void;
  onSent: (tempId: string) => void;
  onSettled: () => Promise<void>;
}) {
  const formRef = useRef<HTMLFormElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [error, setError] = useState<string | null>(null);

  const send = async (formData: FormData) => {
    const body = String(formData.get("body") ?? "").trim();
    const file = (formData.get("attachment") as File | null) ?? null;
    const hasFile = Boolean(file && file.size > 0);
    if (!body && !hasFile) return;

    if (hasFile && file) {
      const rejected = chatAttachmentError(file);
      if (rejected) {
        setError(rejected);
        if (fileInputRef.current) fileInputRef.current.value = "";
        setSelectedFile(null);
        return;
      }
    }

    const tempId = `${OPTIMISTIC_PREFIX}${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    const fileForRetry = hasFile ? file : selectedFile;
    onOptimistic(
      {
        id: tempId,
        collabId,
        sender: viewer,
        body: body || null,
        eventType: null,
        attachments: hasFile
          ? [
              {
                id: tempId,
                name: file!.name,
                mime: file!.type || "application/octet-stream",
                bytes: file!.size,
              },
            ]
          : [],
        redacted: false,
        flags: [],
        createdAt: new Date(),
      } as MessageRow,
      { body, file: fileForRetry },
    );

    // Clear immediately — messenger feel.
    formRef.current?.reset();
    setSelectedFile(null);
    if (textareaRef.current) textareaRef.current.style.height = "";
    if (fileInputRef.current) fileInputRef.current.value = "";
    setError(null);

    const result = await sendMessageAction({}, formData);
    if (result.error && !result.ok) {
      onFailed(tempId);
      setError("Message could not be sent. Tap the red icon to retry.");
      return;
    }
    // Scrubbing is shown on the bubble ("Contact details hidden") — no banner.
    await onSettled();
    // Drop after sync so merge can replace without a gap; clears orphans if body was scrubbed.
    onSent(tempId);
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLTextAreaElement>) => {
    if (event.key !== "Enter" || event.shiftKey) return;
    event.preventDefault();
    const body = textareaRef.current?.value.trim();
    if (!body && !selectedFile) return;
    formRef.current?.requestSubmit();
  };

  return (
    <form ref={formRef} action={send}>
      <input type="hidden" name="collabId" value={collabId} />

      {selectedFile ? (
        <div className="mb-2 flex items-center gap-2 px-1">
          <span className="inline-flex min-w-0 items-center gap-1.5 rounded-full bg-canvas px-2.5 py-1 text-xs text-ink-muted ring-1 ring-line">
            <PaperclipIcon className="size-3.5 shrink-0" />
            <span className="truncate">{selectedFile.name}</span>
            <button
              type="button"
              onClick={() => {
                setSelectedFile(null);
                if (fileInputRef.current) fileInputRef.current.value = "";
              }}
              className="shrink-0 text-ink-subtle hover:text-ink"
              aria-label="Remove attachment"
            >
              ×
            </button>
          </span>
        </div>
      ) : null}

      <div className="flex items-center gap-2">
        <div className="flex flex-1 items-start rounded-3xl border border-line bg-canvas/50 py-1 pl-1.5 pr-2 transition focus-within:border-brand-500 focus-within:bg-surface">
          <input
            ref={fileInputRef}
            type="file"
            name="attachment"
            accept="image/png,image/jpeg,image/webp,image/gif,application/pdf"
            className="sr-only"
            onChange={(event) => {
              const file = event.currentTarget.files?.[0] ?? null;
              if (!file) {
                setSelectedFile(null);
                return;
              }
              const rejected = chatAttachmentError(file);
              if (rejected) {
                setError(rejected);
                setSelectedFile(null);
                event.currentTarget.value = "";
                return;
              }
              setError(null);
              setSelectedFile(file);
            }}
          />
          <button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            aria-label="Attach a file"
            className="mt-0.5 inline-flex size-9 shrink-0 items-center justify-center rounded-full text-ink-subtle transition hover:bg-canvas hover:text-ink"
          >
            <PaperclipIcon className="size-[18px]" />
          </button>
          <textarea
            ref={textareaRef}
            name="body"
            rows={1}
            placeholder="Your message"
            onKeyDown={handleKeyDown}
            onInput={(event) => {
              const el = event.currentTarget;
              el.style.height = "";
              el.style.height = `${Math.min(el.scrollHeight, 120)}px`;
            }}
            className="max-h-[7.5rem] min-h-9 flex-1 resize-none border-0 bg-transparent px-1 py-1.5 text-base leading-5 text-ink outline-none placeholder:text-ink-subtle"
          />
        </div>
        <SendButton />
      </div>

      {error ? <p className="mt-1.5 px-1 text-[11px] leading-snug text-warn-700">{error}</p> : null}
    </form>
  );
}

function SendButton() {
  const { pending } = useFormStatus();
  return (
    <button
      type="submit"
      disabled={pending}
      aria-label="Send message"
      className="inline-flex size-10 shrink-0 items-center justify-center rounded-full bg-brand-600 text-canvas shadow-sm transition hover:bg-brand-700 disabled:opacity-50"
    >
      {pending ? (
        <span className="text-sm leading-none">…</span>
      ) : (
        <SendIcon className="size-3.5" />
      )}
    </button>
  );
}

function DaySeparator({ at }: { at: Date | string }) {
  return (
    <li className="flex justify-center py-3" aria-hidden="true">
      <span className="rounded-full bg-canvas px-2.5 py-1 text-[10px] font-semibold tracking-wide text-ink-subtle uppercase ring-1 ring-line/70">
        {formatDayLabel(at)}
      </span>
    </li>
  );
}

/** Only money and trouble earn colour; everything else stays quiet. */
const DOT_TONES: Record<string, string> = {
  money: "bg-money-600",
  warn: "bg-alert-600",
};

function ActivityLine({
  collabId,
  message,
  names,
  viewer,
  actorParty = null,
  connectTop,
  connectBottom,
  showAllowCall = false,
}: {
  collabId: string;
  message: MessageRow;
  names: { creator: string; brand: string };
  viewer: "creator" | "brand" | "admin";
  actorParty?: Party | null;
  connectTop?: boolean;
  connectBottom?: boolean;
  showAllowCall?: boolean;
}) {
  const router = useRouter();
  const [allowing, setAllowing] = useState(false);
  const [allowError, setAllowError] = useState<string | null>(null);
  const event = parseSystemEvent(message.body ?? "", names, viewer, actorParty);
  const dot = DOT_TONES[event.tone] ?? "bg-line-strong";
  const attachments = readAttachments(message.attachments);
  const audioAttachments = attachments.filter((item) => item.mime.startsWith("audio/"));

  async function allowCall() {
    if (allowing) return;
    setAllowing(true);
    setAllowError(null);
    const form = new FormData();
    form.set("collabId", collabId);
    const result = await enableCallsAction({}, form);
    setAllowing(false);
    if (result.error) {
      setAllowError(result.error);
      return;
    }
    router.refresh();
  }

  return (
    <li className="flex items-stretch gap-3 px-2" role="status">
      {/* Dot stays on the title row — not vertically centered when a player sits below. */}
      <span
        className="relative mt-[0.85rem] flex w-1.5 shrink-0 justify-center self-stretch"
        aria-hidden="true"
      >
        {connectTop ? (
          <span className="absolute bottom-full left-1/2 h-[0.85rem] w-px -translate-x-1/2 bg-line" />
        ) : null}
        {connectBottom ? (
          <span className="absolute top-1.5 bottom-0 left-1/2 w-px -translate-x-1/2 bg-line" />
        ) : null}
        <span className={`absolute top-0 size-1.5 rounded-full ring-2 ring-surface ${dot}`} />
      </span>

      <div className="min-w-0 flex-1 py-1.5">
        <div className="flex items-baseline gap-3">
          <p className="min-w-0 flex-1 text-xs leading-snug text-ink-muted">
            <span className="font-medium text-ink">{event.title}</span>
            {event.detail ? <> · {event.detail}</> : null}
          </p>
          <time
            className="tabular shrink-0 text-[10px] text-ink-subtle"
            dateTime={new Date(message.createdAt).toISOString()}
          >
            {formatTime(message.createdAt)}
          </time>
        </div>
        {showAllowCall ? (
          <div className="mt-2 flex flex-wrap items-center gap-2">
            <button
              type="button"
              disabled={allowing}
              onClick={() => void allowCall()}
              className="inline-flex h-7 min-w-[4rem] items-center justify-center rounded-full bg-ink px-3 text-xs font-bold text-canvas transition hover:opacity-90 disabled:opacity-70"
            >
              {allowing ? "…" : "Allow"}
            </button>
            {allowError ? <p className="text-[11px] font-medium text-alert-600">{allowError}</p> : null}
          </div>
        ) : null}
        {audioAttachments.length > 0 ? (
          <ul className="mt-1.5 list-none space-y-2 p-0">
            {audioAttachments.map((attachment) => (
              <li key={attachment.id} className="pl-0">
                <AttachmentBlock collabId={collabId} attachment={attachment} mine={false} />
              </li>
            ))}
          </ul>
        ) : null}
      </div>
    </li>
  );
}

function ChatBubble({
  collabId,
  message,
  mine,
  author,
  showReadReceipts = false,
  read = false,
  onResend,
}: {
  collabId: string;
  message: MessageRow;
  mine: boolean;
  author: string;
  showReadReceipts?: boolean;
  read?: boolean;
  onResend?: () => void;
}) {
  const attachments = readAttachments(message.attachments);
  const hasBody = Boolean(message.body?.trim());
  const failed = message.clientStatus === "failed";
  const sending = message.clientStatus === "sending";

  return (
    <li
      className={`flex flex-col py-1.5 ${mine ? "items-end pl-14" : "items-start pr-14"}`}
    >
      <div className="flex max-w-[min(85%,28rem)] items-end gap-2">
        {failed && mine ? (
          <button
            type="button"
            onClick={onResend}
            className="mb-0.5 shrink-0 text-alert-600 hover:text-alert-700"
            aria-label="Resend message"
            title="Tap to retry"
          >
            <FailIcon />
          </button>
        ) : null}
        {/* Inline colors: theme tokens + Tailwind utilities flip on mobile dark mode.
            w-max + min-w keeps short “Hi” bubbles wide enough for the time row. */}
        <div
          className={`w-max min-w-[5.5rem] max-w-full shrink rounded-2xl px-3.5 py-2 ${
            mine ? "rounded-br-md" : "rounded-bl-md"
          }`}
          style={{
            backgroundColor: "#000000",
            color: "#ffffff",
            boxShadow: "0 0 0 1px rgba(255,255,255,0.14), 0 1px 2px rgba(0,0,0,0.4)",
          }}
        >
          {!mine ? (
            <p
              className="mb-0.5 truncate text-[11px] font-medium"
              style={{ color: "rgba(255,255,255,0.55)" }}
            >
              {author}
            </p>
          ) : null}
          {hasBody ? (
            <p className="text-sm leading-relaxed whitespace-pre-line" style={{ color: "#ffffff" }}>
              {message.body}
            </p>
          ) : null}
          {attachments.length > 0 ? (
            <ul className={`space-y-2 ${hasBody ? "mt-2" : ""}`}>
              {attachments.map((attachment) => (
                <li key={attachment.id}>
                  <AttachmentBlock collabId={collabId} attachment={attachment} mine />
                </li>
              ))}
            </ul>
          ) : null}
          <p className="mt-1 flex items-center justify-end gap-1">
            <time
              className="whitespace-nowrap text-[10px] tabular"
              style={{ color: "rgba(255,255,255,0.45)" }}
              dateTime={new Date(message.createdAt).toISOString()}
              title={formatDateTime(message.createdAt)}
            >
              {formatTime(message.createdAt)}
            </time>
            {sending ? <ClockIcon /> : null}
            {showReadReceipts ? <ReceiptTicks read={read} /> : null}
          </p>
          {message.redacted ? (
            <p className="mt-1 text-[11px] font-medium text-warn-600">
              Contact details hidden
            </p>
          ) : null}
        </div>
      </div>
      {failed ? (
        <button
          type="button"
          onClick={onResend}
          className="mt-1 px-1 text-[11px] font-semibold text-alert-600 hover:text-alert-700"
        >
          Not delivered · Tap to retry
        </button>
      ) : null}
    </li>
  );
}

/** Thin WhatsApp-style ticks — single = sent, double = seen. */
function ReceiptTicks({ read }: { read: boolean }) {
  const stroke = {
    stroke: "currentColor",
    strokeWidth: 1.25,
    strokeLinecap: "round" as const,
    strokeLinejoin: "round" as const,
  };
  return (
    <span
      className="inline-flex shrink-0 self-center"
      style={{ color: read ? "#7dd3fc" : "rgba(255,255,255,0.38)" }}
      title={read ? "Seen" : "Sent"}
      aria-label={read ? "Seen" : "Sent"}
    >
      <svg
        viewBox={read ? "0 0 14 9" : "0 0 10 9"}
        className={read ? "h-[9px] w-[14px]" : "h-[9px] w-[10px]"}
        fill="none"
        aria-hidden="true"
      >
        {read ? (
          <>
            <path d="M0.75 4.6 3.1 7.1 8.35 1.25" {...stroke} />
            <path d="M5.4 7.1 12.85 1.25" {...stroke} />
          </>
        ) : (
          <path d="M0.75 4.6 3.1 7.1 8.85 1.25" {...stroke} />
        )}
      </svg>
    </span>
  );
}

type MessageAttachmentMeta = {
  id: string;
  name: string;
  mime: string;
  bytes: number;
};

function readAttachments(raw: unknown): MessageAttachmentMeta[] {
  if (!Array.isArray(raw)) return [];
  return raw.filter(
    (item): item is MessageAttachmentMeta =>
      typeof item === "object" &&
      item !== null &&
      typeof (item as MessageAttachmentMeta).id === "string" &&
      typeof (item as MessageAttachmentMeta).name === "string" &&
      typeof (item as MessageAttachmentMeta).mime === "string",
  );
}

const AUDIO_RATES = [1, 1.5, 2] as const;

function formatAudioTime(seconds: number): string {
  if (!Number.isFinite(seconds) || seconds < 0) return "0:00";
  const total = Math.floor(seconds);
  const m = Math.floor(total / 60);
  const s = total % 60;
  return `${m}:${s.toString().padStart(2, "0")}`;
}

/**
 * Cross-origin `<audio src>` often omits cookies; fetch with credentials and
 * play from a blob URL so call recordings / voice notes work on the live API host.
 * Scrub + speed match WhatsApp-style voice notes.
 */
function AuthenticatedAudio({ href, label }: { href: string; label: string }) {
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const [src, setSrc] = useState<string | null>(null);
  const [error, setError] = useState(false);
  const [playing, setPlaying] = useState(false);
  const [position, setPosition] = useState(0);
  const [duration, setDuration] = useState(0);
  const [rate, setRate] = useState<(typeof AUDIO_RATES)[number]>(1);

  useEffect(() => {
    let objectUrl: string | null = null;
    let cancelled = false;

    void (async () => {
      try {
        const res = await fetch(href, { credentials: "include", headers: { Accept: "*/*" } });
        if (!res.ok) throw new Error(`audio ${res.status}`);
        const blob = await res.blob();
        if (cancelled) return;
        objectUrl = URL.createObjectURL(blob);
        setSrc(objectUrl);
      } catch {
        if (!cancelled) setError(true);
      }
    })();

    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [href]);

  useEffect(() => {
    const el = audioRef.current;
    if (!el) return;
    el.playbackRate = rate;
  }, [rate, src]);

  if (error) {
    return <p className="m-0 text-xs text-ink-muted">Could not load audio.</p>;
  }
  if (!src) {
    return <p className="m-0 text-xs text-ink-muted">Loading audio…</p>;
  }

  const progress = duration > 0 ? Math.min(1, position / duration) : 0;
  const rateLabel = rate === 1 ? "1×" : rate === 1.5 ? "1.5×" : "2×";

  async function toggle() {
    const el = audioRef.current;
    if (!el) return;
    if (el.paused) {
      await el.play().catch(() => undefined);
    } else {
      el.pause();
    }
  }

  function cycleRate() {
    const idx = AUDIO_RATES.indexOf(rate);
    const next = AUDIO_RATES[(idx + 1) % AUDIO_RATES.length]!;
    setRate(next);
  }

  return (
    <div
      className="flex w-full max-w-sm items-center gap-2.5 rounded-2xl bg-canvas/60 px-2.5 py-2 ring-1 ring-line/70"
      aria-label={label}
    >
      <audio
        ref={audioRef}
        src={src}
        preload="metadata"
        className="hidden"
        onPlay={() => setPlaying(true)}
        onPause={() => setPlaying(false)}
        onEnded={() => {
          setPlaying(false);
          setPosition(0);
        }}
        onTimeUpdate={(event) => setPosition(event.currentTarget.currentTime)}
        onLoadedMetadata={(event) => setDuration(event.currentTarget.duration || 0)}
      />
      <button
        type="button"
        onClick={() => void toggle()}
        aria-label={playing ? "Pause" : "Play"}
        className="inline-flex size-8 shrink-0 items-center justify-center rounded-full bg-ink text-canvas transition hover:opacity-90"
      >
        {playing ? (
          <svg viewBox="0 0 16 16" className="size-3.5" fill="currentColor" aria-hidden="true">
            <path d="M4.5 3h2.5v10H4.5V3Zm4.5 0H11.5v10H9V3Z" />
          </svg>
        ) : (
          <svg viewBox="0 0 16 16" className="size-3.5 translate-x-px" fill="currentColor" aria-hidden="true">
            <path d="M5 3.2v9.6L13 8 5 3.2Z" />
          </svg>
        )}
      </button>
      <div className="min-w-0 flex-1">
        <input
          type="range"
          min={0}
          max={duration || 1}
          step={0.05}
          value={Math.min(position, duration || 0)}
          aria-label="Scrub voice note"
          onChange={(event) => {
            const next = Number(event.currentTarget.value);
            setPosition(next);
            if (audioRef.current) audioRef.current.currentTime = next;
          }}
          className="audio-scrub m-0 h-1.5 w-full cursor-pointer appearance-none rounded-full bg-line accent-ink"
          style={{
            background: `linear-gradient(to right, var(--color-ink) ${progress * 100}%, var(--color-line) ${progress * 100}%)`,
          }}
        />
        <div className="mt-1 flex items-center justify-between gap-2">
          <span className="tabular text-[11px] font-semibold text-ink-muted">
            {formatAudioTime(playing || position > 0 ? position : duration)}
          </span>
          <button
            type="button"
            onClick={cycleRate}
            aria-label={`Playback speed ${rateLabel}`}
            className="rounded-md bg-surface px-1.5 py-0.5 text-[11px] font-bold tabular-nums text-ink-muted ring-1 ring-line transition hover:text-ink"
          >
            {rateLabel}
          </button>
        </div>
      </div>
    </div>
  );
}

function AttachmentBlock({
  collabId,
  attachment,
  mine,
}: {
  collabId: string;
  attachment: MessageAttachmentMeta;
  mine: boolean;
}) {
  const href = `${publicApiUrl()}/api/collabs/${collabId}/messages/${attachment.id}/file`;
  const isImage = attachment.mime.startsWith("image/");
  const isAudio = attachment.mime.startsWith("audio/");

  if (isImage) {
    return (
      <a
        href={href}
        target="_blank"
        rel="noreferrer"
        className={`block overflow-hidden rounded-lg ring-1 ${
          mine ? "ring-white/20" : "ring-line/70"
        }`}
      >
        {/* eslint-disable-next-line @next/next/no-img-element */}
        <img
          src={href}
          alt={attachment.name}
          loading="lazy"
          decoding="async"
          className="max-h-48 w-full bg-black/5 object-cover"
        />
      </a>
    );
  }

  if (isAudio) {
    return (
      <AuthenticatedAudio
        href={href}
        label={attachment.name || "Call recording"}
      />
    );
  }

  return (
    <a
      href={href}
      target="_blank"
      rel="noreferrer"
      className={`flex items-center gap-2 rounded-lg px-2.5 py-2 text-xs ring-1 ${
        mine ? "bg-white/10 text-white ring-white/20" : "bg-canvas text-ink ring-line/70"
      }`}
    >
      <span
        className={`inline-flex size-7 shrink-0 items-center justify-center rounded-md text-[9px] font-bold ${
          mine ? "bg-white/15 text-white" : "bg-surface text-ink-muted ring-1 ring-line"
        }`}
      >
        {fileBadge(attachment.mime)}
      </span>
      <span className="min-w-0 flex-1">
        <span className="block truncate font-medium">{attachment.name}</span>
        <span className={`block truncate ${mine ? "text-white/50" : "text-ink-subtle"}`}>
          {formatFileSize(attachment.bytes)}
        </span>
      </span>
    </a>
  );
}

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function fileBadge(mime: string): string {
  if (mime === "application/pdf") return "PDF";
  if (mime.startsWith("image/")) return "IMG";
  if (mime.startsWith("audio/")) return "AUD";
  return "FILE";
}

function FailIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" className="size-5" aria-hidden="true">
      <path d="M12 2a10 10 0 1 0 0 20 10 10 0 0 0 0-20Zm-1 5h2v7h-2V7Zm0 9h2v2h-2v-2Z" />
    </svg>
  );
}

function ClockIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.75"
      strokeLinecap="round"
      strokeLinejoin="round"
      className="size-3 opacity-50"
      aria-hidden="true"
    >
      <circle cx="12" cy="12" r="9" />
      <path d="M12 7v5l3 2" />
    </svg>
  );
}

function LockIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.75"
      strokeLinecap="round"
      strokeLinejoin="round"
      className="size-3"
      aria-hidden="true"
    >
      <rect x="5" y="11" width="14" height="10" rx="2" />
      <path d="M8 11V8a4 4 0 0 1 8 0v3" />
    </svg>
  );
}

function PaperclipIcon({ className }: { className?: string }) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.75"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
      aria-hidden="true"
    >
      <path d="m21.44 11.05-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48" />
    </svg>
  );
}

function SendIcon({ className }: { className?: string }) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
      aria-hidden="true"
    >
      <path d="M12 19V5" />
      <path d="m5 12 7-7 7 7" />
    </svg>
  );
}
